import { type CurrencyCode, requireCurrencyCode } from './currency.ts'
import { formatScaled, parseScaled } from './decimal.ts'
import type { Money } from './money.ts'

export const RATE_SCALE = 10

/** `numeric(20,10)`: ten integer digits, ten fraction digits. */
export const RATE_PATTERN = /^-?\d{1,10}(\.\d{1,10})?$/

declare const RateBrand: unique symbol

/**
 * An exchange rate held as integer units at {@link RATE_SCALE}.
 * Conversion is a server operation; this type carries and renders a rate, it never applies one.
 */
export interface Rate {
  readonly units: bigint
  readonly from: CurrencyCode
  readonly to: CurrencyCode
  readonly [RateBrand]: true
}

export interface ApiRate {
  readonly rate: string
  readonly from: string
  readonly to: string
}

export function rate(value: string, from: CurrencyCode, to: CurrencyCode): Rate {
  return { units: parseScaled(value, RATE_SCALE, RATE_PATTERN), from, to } as Rate
}

export function fromApiRate(value: ApiRate): Rate {
  return rate(value.rate, requireCurrencyCode(value.from), requireCurrencyCode(value.to))
}

export function toApiRate(value: Rate): ApiRate {
  return { rate: formatScaled(value.units, RATE_SCALE), from: value.from, to: value.to }
}

/**
 * A loan's `annualInterestRate` is the one `Rate` reuse that is a percentage, not a rate -
 * the wire carries the fraction (`0.05` for five per cent) while the value a user types and
 * sees stays the percent (`5`), the same split `Ratio` has for a target's ratio metric. These
 * two functions do that scaling at the API boundary so `Rate`'s own value never changes shape.
 */
export function toApiRateFraction(value: Rate): ApiRate {
  return { rate: formatScaled(value.units / 100n, RATE_SCALE), from: value.from, to: value.to }
}

export function fromApiRateFraction(value: ApiRate): Rate {
  const fractionUnits = parseScaled(value.rate, RATE_SCALE, RATE_PATTERN)
  return rate(
    formatScaled(fractionUnits * 100n, RATE_SCALE),
    requireCurrencyCode(value.from),
    requireCurrencyCode(value.to),
  )
}

export function compareRate(a: Rate, b: Rate): -1 | 0 | 1 {
  return a.units < b.units ? -1 : a.units > b.units ? 1 : 0
}

/**
 * The ratio implied by two independently known amounts, for display only. Never applied to
 * derive either amount - it only reads back what the two amounts already say. Bigint
 * division throughout: a `Number()` cast here would be the same precision loss the domain
 * package exists to rule out.
 */
export function impliedRate(source: Money, target: Money): Rate | undefined {
  if (source.units <= 0n || target.units <= 0n) return undefined
  const rateUnits = (target.units * 10n ** BigInt(RATE_SCALE)) / source.units
  return rate(formatScaled(rateUnits, RATE_SCALE), source.currency, target.currency)
}
